import {
  AgentInLenderContext,
  getAgentById,
  getAgentInLenderContextByAgentId,
  patchSetAgentIdInLenderContextById,
  postAgentInLenderContext,
} from '@nemuru-eng/js-api-client';
import { v4 as uuidv4 } from 'uuid';

import { getCredentialAuthPayloadFields } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/maps/credentialApiPayload.map';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

import { AddAgentInLenderContextCredentialsFormType as AddCredentialsFormType } from '../schemas/AddAgentInLenderContextCredentialsFormSchema';

interface useAddOrEditAgentInLenderContextProps {
  agentId: string;
  selectedAgentInLenderContext?: AgentInLenderContext;
}

export const useAddOrEditAgentInLenderContext = ({
  agentId,
  selectedAgentInLenderContext,
}: useAddOrEditAgentInLenderContextProps) => {
  const { request, loading } = useRequest();
  const { revalidate } = useCache(getAgentInLenderContextByAgentId, { agentId });
  const { data: agent } = useCache(getAgentById, { agentId });

  const createOrUpdateAgentInLenderContext = async (formValues: AddCredentialsFormType) => {
    if (selectedAgentInLenderContext) {
      const agentInLenderContextRequest = getRequestPayload(formValues, selectedAgentInLenderContext.id);
      await request(patchSetAgentIdInLenderContextById, agentInLenderContextRequest);
    } else {
      const agentInLenderContextRequest = getRequestPayload(formValues);
      await request(postAgentInLenderContext, agentInLenderContextRequest);
    }
    revalidate();
  };

  const getRequestPayload = (
    formValues: AddCredentialsFormType,
    agentInLenderContextId: string | undefined = undefined,
  ) => {
    return {
      agentId,
      id: agentInLenderContextId ?? (uuidv4() as string),
      lenderId: formValues.lenderId,
      program: formValues.program,
      product: formValues.product,
      merchantIdInLenderContext: formValues.merchantIdInLenderContext?.trim(),
      ...(formValues.channelIdInLenderContext && {
        channelIdInLenderContext: formValues.channelIdInLenderContext.trim(),
      }),
      ...(formValues.agentIdInLenderContext && {
        agentIdInLenderContext: formValues.agentIdInLenderContext.trim(),
      }),
      ...(formValues.market && { market: formValues.market.trim() }),
      ...getCredentialAuthPayloadFields(formValues),
      ...(formValues.articleType && { articleType: formValues.articleType }),
    };
  };

  return { createOrUpdateAgentInLenderContext, loading, agent };
};
